import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  Tooltip,
  alpha,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  FilterList,
  FilterListOff,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import DateRangePicker from "../../../components/newFormat/DateRangePicker";
import clienteAxios from "../../../../config/axios";
import { filtrosDefault } from "../constants";

const ventaGasTip = "Requerido para Venta Gas";

const FiltrosFacturacion = ({
  filtros,
  onFiltrosChange,
  onClearFiltros,
  cargando,
}) => {
  const [formasPagoData, setFormasPagoData] = useState([]);
  const [usuariosData, setUsuariosData] = useState([]);
  const [horariosData, setHorariosData] = useState([]);
  const [expandido, setExpandido] = useState(false);
  const [codigoLocal, setCodigoLocal] = useState(filtros.venNumFactura || "");
  const [rucLocal, setRucLocal] = useState(filtros.venRucCliente || "");
  const [clienteLocal, setClienteLocal] = useState(filtros.venNomCliente || "");

  const tieneFiltros = Object.entries(filtros).some(([key, val]) => {
    if (val === null || val === undefined) return false;
    return val !== filtrosDefault[key];
  });

  const tieneFiltrosExtra = !!(
    filtros.venNomCliente ||
    filtros.venFormaPago ||
    filtros.venFacturaCredito ||
    filtros.venEstado ||
    filtros.venEnvio
  );

  useEffect(() => {
    if (tieneFiltrosExtra && !expandido) setExpandido(true);
  }, []);

  useEffect(() => {
    clienteAxios
      .get("/formaPagos/filtro/0")
      .then((res) => {
        if (res.data.data) setFormasPagoData(res.data.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setCodigoLocal(filtros.venNumFactura || "");
    setRucLocal(filtros.venRucCliente || "");
    setClienteLocal(filtros.venNomCliente || "");
  }, [filtros.venNumFactura, filtros.venRucCliente, filtros.venNomCliente]);

  const searchUsuarios = useCallback((val) => {
    clienteAxios
      .get(`proveedores/listar/por/perfil?perfil=USUARIO&search=${val}`)
      .then((res) => {
        setUsuariosData(res.data || []);
      })
      .catch(() => {});
  }, []);

  const fetchHorarios = useCallback((perId) => {
    if (!perId) return;
    clienteAxios
      .get(`horarios/${perId}/ultimos_dias`)
      .then((res) => {
        const mapped = (res.data || []).map(
          (item) => item.desde + " - " + item.hasta
        );
        setHorariosData(mapped);
      })
      .catch(() => {
        setHorariosData([]);
      });
  }, []);

  const creditoOptions = ["SI", "NO"];
  const envioOptions = ["NO", "ENVIADO", "ERROR"];
  const estadoOptions = [
    "ACTIVA",
    "ANULADA",
    "SIN AUTORIZAR",
    "GENERAR XML",
    "AUTORIZADO",
    "PENDIENTE",
  ];

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      fontSize: "0.75rem",
      py: 0.2,
      px: 0.5,
      "& fieldset": { borderColor: "divider" },
      "&:hover fieldset": {
        borderColor: (t) => alpha(t.palette.primary.main, 0.3),
      },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
    "& input": { py: 0.3, px: 0.5, fontSize: "0.75rem" },
    minWidth: 110,
    maxWidth: 170,
  };

  const ventaGasDotSx = {
    position: "absolute",
    top: -2,
    right: -2,
    width: 7,
    height: 7,
    borderRadius: "50%",
    bgcolor: "warning.main",
    border: "1px solid",
    borderColor: "background.paper",
  };

  const submitCodigo = () => {
    if (codigoLocal !== (filtros.venNumFactura || "")) {
      onFiltrosChange({ venNumFactura: codigoLocal });
    }
  };

  const submitRuc = () => {
    if (rucLocal !== (filtros.venRucCliente || "")) {
      onFiltrosChange({ venRucCliente: rucLocal });
    }
  };

  const submitCliente = () => {
    if (clienteLocal !== (filtros.venNomCliente || "")) {
      onFiltrosChange({ venNomCliente: clienteLocal });
    }
  };

  return (
    <Box
      sx={{
        px: 1,
        py: 0.3,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: tieneFiltros
          ? (t) => alpha(t.palette.secondary.main, 0.4)
          : "divider",
        bgcolor: tieneFiltros
          ? (t) => alpha(t.palette.secondary.main, 0.05)
          : "transparent",
        transition: "all 0.2s",
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={() => setExpandido(!expandido)}
          disabled={cargando}
          sx={{
            bgcolor: tieneFiltrosExtra
              ? (t) => alpha(t.palette.secondary.main, 0.12)
              : (t) => alpha(t.palette.primary.main, 0.08),
            color: tieneFiltrosExtra ? "secondary.main" : "primary.main",
            border: "1px solid",
            borderColor: tieneFiltrosExtra
              ? (t) => alpha(t.palette.secondary.main, 0.3)
              : (t) => alpha(t.palette.primary.main, 0.2),
            borderRadius: 1.5,
            p: 0.5,
            transition: "all 0.2s",
            "&:hover": {
              bgcolor: tieneFiltrosExtra
                ? (t) => alpha(t.palette.secondary.main, 0.2)
                : (t) => alpha(t.palette.primary.main, 0.15),
            },
          }}
        >
          {expandido ? <ExpandLess /> : <ExpandMore />}
        </IconButton>

        {tieneFiltros && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<FilterListOff />}
            onClick={onClearFiltros}
            disabled={cargando}
            sx={{
              textTransform: "none",
              fontSize: "0.7rem",
              fontWeight: 600,
              px: 1.5,
              py: 0.3,
              borderRadius: 1.5,
              borderWidth: 1.5,
              "&:hover": { borderWidth: 1.5 },
            }}
          >
            Limpiar
          </Button>
        )}

        <FilterList
          sx={{
            fontSize: "1rem",
            color: tieneFiltros ? "secondary.main" : "text.secondary",
          }}
        />

        <Tooltip title={ventaGasTip} followCursor>
          <Box position="relative">
            <Box sx={ventaGasDotSx} />
            <TextField
              size="small"
              placeholder="Documento"
              value={codigoLocal}
              onChange={(e) => setCodigoLocal(e.target.value)}
              onBlur={submitCodigo}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitCodigo();
                }
              }}
              disabled={cargando}
              sx={inputSx}
            />
          </Box>
        </Tooltip>

        <Tooltip title={ventaGasTip} followCursor>
          <Box position="relative">
            <Box sx={ventaGasDotSx} />
            <TextField
              size="small"
              placeholder="Ruc Cliente"
              value={rucLocal}
              onChange={(e) => setRucLocal(e.target.value)}
              onBlur={submitRuc}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitRuc();
                }
              }}
              disabled={cargando}
              sx={inputSx}
            />
          </Box>
        </Tooltip>

        <Tooltip title={ventaGasTip} followCursor>
          <Box position="relative">
            <Box sx={ventaGasDotSx} />
            <DateRangePicker
              desde={filtros.venFechaFactura1}
              hasta={filtros.venFechaFactura2}
              onChange={(d1, d2) =>
                onFiltrosChange({
                  venFechaFactura1: d1 || null,
                  venFechaFactura2: d2 || null,
                })
              }
              disabled={cargando}
              placeholder="F. Factura"
            />
          </Box>
        </Tooltip>

        <Tooltip title={ventaGasTip} followCursor>
          <Box position="relative">
            <Box sx={ventaGasDotSx} />
            <DateRangePicker
              desde={filtros.venFecReg1}
              hasta={filtros.venFecReg2}
              onChange={(d1, d2) =>
                onFiltrosChange({
                  venFecReg1: d1 || null,
                  venFecReg2: d2 || null,
                })
              }
              disabled={cargando}
              placeholder="F. Registro"
            />
          </Box>
        </Tooltip>

        <Tooltip title={ventaGasTip} followCursor>
          <Box position="relative">
            <Box sx={ventaGasDotSx} />
            <Autocomplete
              size="small"
              filterOptions={(x) => x}
              options={usuariosData}
              getOptionLabel={(u) =>
                u ? `${u.perNombre || ""} - ${u.perDocNumero || ""}` : ""
              }
              value={null}
              onChange={(_, val) => {
                setHorariosData([]);
                onFiltrosChange({
                  venUser: val ? val.perDocNumero : "",
                  horarios: "",
                });
                if (val && val.perId) {
                  fetchHorarios(val.perId);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={filtros.venUser || "Usuario"}
                  sx={inputSx}
                  onChange={(e) => searchUsuarios(e.target.value)}
                />
              )}
              disabled={cargando}
              sx={{ minWidth: 130, maxWidth: 180 }}
            />
          </Box>
        </Tooltip>

        {horariosData.length > 0 && (
          <Autocomplete
            size="small"
            options={horariosData}
            value={filtros.horarios || ""}
            onChange={(_, val) => onFiltrosChange("horarios", val || "")}
            renderInput={(params) => (
              <TextField {...params} placeholder="Horario" sx={inputSx} />
            )}
            disabled={cargando}
            sx={{ minWidth: 130, maxWidth: 200 }}
          />
        )}
      </Box>

      <Collapse in={expandido}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ py: 0.5, flexWrap: "wrap" }}
        >
          <TextField
            size="small"
            placeholder="Cliente"
            value={clienteLocal}
            onChange={(e) => setClienteLocal(e.target.value)}
            onBlur={submitCliente}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitCliente();
              }
            }}
            disabled={cargando}
            sx={inputSx}
          />

          <Autocomplete
            size="small"
            options={formasPagoData.map((f) => f.fpagoNombre)}
            value={filtros.venFormaPago || ""}
            onChange={(_, val) => onFiltrosChange("venFormaPago", val || "")}
            renderInput={(params) => (
              <TextField {...params} placeholder="F. Pago" sx={inputSx} />
            )}
            disabled={cargando}
            sx={{ minWidth: 110, maxWidth: 150 }}
          />

          <Autocomplete
            size="small"
            options={creditoOptions}
            value={filtros.venFacturaCredito || ""}
            onChange={(_, val) =>
              onFiltrosChange("venFacturaCredito", val || "")
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Crédito" sx={inputSx} />
            )}
            disabled={cargando}
            sx={{ minWidth: 90, maxWidth: 120 }}
          />

          <Autocomplete
            size="small"
            options={estadoOptions}
            value={filtros.venEstado || ""}
            onChange={(_, val) => onFiltrosChange("venEstado", val || "")}
            renderInput={(params) => (
              <TextField {...params} placeholder="Estado" sx={inputSx} />
            )}
            disabled={cargando}
            sx={{ minWidth: 110, maxWidth: 150 }}
          />

          <Autocomplete
            size="small"
            options={envioOptions}
            value={filtros.venEnvio || ""}
            onChange={(_, val) => onFiltrosChange("venEnvio", val || "")}
            renderInput={(params) => (
              <TextField {...params} placeholder="Envio" sx={inputSx} />
            )}
            disabled={cargando}
            sx={{ minWidth: 100, maxWidth: 130 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(FiltrosFacturacion);
